import { useCallback, useDeferredValue, useEffect, useMemo, useRef, useState } from 'react';
import api from '@/lib/api';
import { DEFAULT_SHOP_DRAWING_DISCIPLINES, normalizeShopDrawingDisciplines, useAppSettings } from '@/contexts/AppSettingsContext';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { PageHeader } from '@/components/ui/page-header';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { StatusPill } from '@/components/ui/status-pill';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useConfirm } from '@/components/ui/confirm-dialog';
import { notify } from '@/components/ui/notify';
import { useTabParam, useUrlParam, useUrlState } from '@/hooks/useUrlState';
import { getErrorMessage } from '@/lib/errors';
import { formatDate, formatNumber } from '@/lib/format';
import { getStatusTone } from '@/lib/status';
import { parseSortParam, formatSortParam } from '@/lib/table';
import { cn } from '@/lib/utils';
import { canEdit, hasPermission } from '@/lib/permissions';
import {
  ChevronDown,
  Copy,
  Download,
  FileDown,
  FileSpreadsheet,
  Files,
  FolderOpen,
  Link2,
  Pencil,
  Plus,
  Send,
  Trash2,
  Upload,
  UserPlus,
} from 'lucide-react';
import { sdRequest } from '@/components/shop-drawings/api';
import { AllDrawingsTab } from '@/components/shop-drawings/AllDrawingsTab';
import { BlockedTab } from '@/components/shop-drawings/BlockedTab';
import { TidyDialog } from '@/components/shop-drawings/TidyRegister';
import { BulkSubmissionDialog } from '@/components/shop-drawings/BulkSubmissionDialog';
import { DrawingEditPanel, DrawingPanel, createDrawingDraft } from '@/components/shop-drawings/DrawingPanel';
import { PipelineStrip } from '@/components/shop-drawings/PipelineStrip';
import { EMPTY_LOG_FILTERS, SubmissionLogTab, replyViewOf } from '@/components/shop-drawings/SubmissionLogTab';
import {
  downloadXlsx,
  drawingsToXlsx,
  parseImportFile,
  submissionsToXlsx,
  templateToXlsx,
} from '@/components/shop-drawings/excel';
import {
  BLOCKER_LABELS,
  EMPTY_ALL_FILTERS,
  GROUP_PAGE_SIZES,
  REPLY_CODE_OF,
  STATUSES,
  STATUS_LABELS,
  ageBucket,
  blockedViewOrder,
  buildSubmissionRows,
  computePipeline,
  drawingTitle,
  filterBlocked,
  filterDrawings,
  filterSubmissionRows,
  labelize,
  latestChange,
  orderSubmissionRows,
  submittedNotLogged,
  tidySuggestions,
  toDayFirst,
  uniqueValues,
  valueKey,
} from '@/components/shop-drawings/model';
import {
  BulkBar,
  BulkButton,
  BulkDivider,
  DisciplineChip,
  tableClass,
  tdClass,
  thClass,
} from '@/components/shop-drawings/SdUi';

const DISCIPLINES = DEFAULT_SHOP_DRAWING_DISCIPLINES;
const DRAWING_TYPES = ['Floor Plan', 'Wiring Diagram', 'Panel Schedule', 'Single Line', 'Schematic'];
const BULK_STATUS_PLACEHOLDER = '__bulk_status__';
const BULK_RESP_PLACEHOLDER = '__bulk_resp__';
const DEFAULT_PAGE_SIZE = 50;
const TAB_VALUES = ['all', 'blocked', 'submission-log'];
// The API takes at most this many drawings per Duplicate / Log submission request.
const BULK_LIMIT = 500;
const EMPTY_BLOCKED_FILTERS = { reason: null, project: null, disc: null, owner: null, search: '' };
const MENU_ITEM = 'h-[34px] gap-2.5 rounded-lg px-2.5 text-[13px] text-ink focus:bg-surface-hover focus:text-ink [&>svg]:size-[15px] [&>svg]:text-ink-3';
// All Drawings view state in the URL: filters, search, sort and grouping (a reload or a copied link
// brings the same view back).
const ALL_URL_STATE = { ...EMPTY_ALL_FILTERS, sort: 'updated:desc', group: 'project' };
const ADMIN_ROLES = new Set(['Super Admin', 'Admin']);

// /api/shop-drawings, with the legacy /api/shop_drawings prefix as a fallback (components/shop-drawings/api.js).
const shopDrawingsRequest = sdRequest;

const isNotFoundError = (err) => err.response?.status === 404;

const errorDetail = (err, fallback) => {
  const message = getErrorMessage(err, fallback);
  return message === 'Not Found' ? fallback : message;
};

/** A bulk request over the 500-drawing limit answers 422 with a raw pydantic message; say it plainly. */
const bulkErrorMessage = (err, fallback) => {
  const detail = err?.response?.data?.detail;
  const overLimit = err?.response?.status === 422
    && Array.isArray(detail)
    && detail.some((item) => (item?.loc || []).includes('ids') || (item?.loc || []).includes('items') || /at most/i.test(String(item?.msg || '')));
  return overLimit
    ? `This works on up to ${formatNumber(BULK_LIMIT, { maxDecimals: 0 })} drawings at a time. Select fewer and try again.`
    : getErrorMessage(err, fallback);
};

const fmt = (value) => formatNumber(value, { maxDecimals: 0 });
const plural = (count, one, many = `${one}s`) => `${fmt(count)} ${count === 1 ? one : many}`;

/** History note + toasts for a blocked reason; 'NONE' (the Board's "Reason missing" column) reads as cleared. */
const reasonWords = (blocker) => {
  if (!blocker || blocker === 'NONE') {
    return { note: 'Blocker reason cleared', single: 'Reason cleared', many: (count) => `Reason cleared on ${plural(count, 'drawing')}` };
  }
  const label = labelize(blocker, BLOCKER_LABELS);
  return { note: `Blocker reason set to ${label}`, single: `Reason “${label}” saved`, many: (count) => `Reason “${label}” set on ${plural(count, 'drawing')}` };
};

const mergeUpdated = (current, updatedItems) => {
  if (!updatedItems.length) return current;
  const byId = new Map(updatedItems.map((item) => [item.id, item]));
  return current.map((item) => byId.get(item.id) || item);
};

const copyText = async (text) => {
  if (navigator.clipboard?.writeText) return navigator.clipboard.writeText(text);
  const area = document.createElement('textarea');
  area.value = text;
  document.body.appendChild(area);
  area.select();
  document.execCommand('copy');
  area.remove();
  return undefined;
};

export default function ShopDrawingsPage() {
  const { user } = useAuth();
  const { settings } = useAppSettings();
  const confirm = useConfirm();
  const isAdmin = ADMIN_ROLES.has(String(user?.role || '').trim());
  // BUG-002: a role whose Shop Drawings level is View (Admin › Access) — or reached through Tasks on View — and Viewer
  // accounts read the register without any write control: no Add drawing / Import, plain status badges, no row edits,
  // no check boxes or bulk bars, no reply / submission forms, no chase or request emails. The server refuses those
  // writes anyway (edit_level_guard('shop_drawings')).
  const readOnly = !canEdit(user, 'shop_drawings');
  // 13 idea 3: a project group links to its project. Drawings carry the project as typed text (no id), so the
  // name is matched to the project list (ignoring case and spacing); a name several projects share (one per
  // system) opens the Projects list searched for it. Only for people who can open projects.
  const canOpenProjects = hasPermission(user, 'projects');
  const [projectIds, setProjectIds] = useState(null);
  useEffect(() => {
    if (!canOpenProjects) return undefined;
    let alive = true;
    api.get('/projects')
      .then((res) => {
        if (!alive) return;
        const index = new Map();
        (Array.isArray(res.data) ? res.data : []).forEach((project) => {
          const key = valueKey(project?.name);
          if (key && project?.id) index.set(key, [...(index.get(key) || []), project.id]);
        });
        setProjectIds(index);
      })
      .catch(() => {});
    return () => { alive = false; };
  }, [canOpenProjects]);
  const projectHref = useCallback((name) => {
    const ids = projectIds?.get(valueKey(name));
    if (!ids?.length) return null;
    return ids.length === 1
      ? `/projects/${encodeURIComponent(ids[0])}`
      : `/projects?status=all&q=${encodeURIComponent(String(name).trim())}`;
  }, [projectIds]);
  // The active tab lives in the URL (?tab=blocked | submission-log), so links such as the
  // Dashboard's "Blocked drawings" KPI open the right tab and a reload keeps it.
  const [activeTab, setActiveTab] = useTabParam(TAB_VALUES, { defaultTab: 'all' });
  const [allState, setAllState] = useUrlState(ALL_URL_STATE);
  const [drawingParam, setDrawingParam] = useUrlParam('drawing', '');
  // 13 #10 List | Matrix (All Drawings) and 14 #9 List | Board (Blocked): ?view=matrix | board.
  const [viewParam, setViewParam] = useUrlParam('view', '');
  // ?owner=<name> (the Sunday digest notification): the Blocked tab filtered to that owner.
  const [ownerParam, setOwnerParam] = useUrlParam('owner', '');
  const [drawings, setDrawings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [allPage, setAllPage] = useState(1);
  const [allPageSize, setAllPageSize] = useState(DEFAULT_PAGE_SIZE);
  // Grouped by project, a page holds this many projects (13 #5).
  const [groupPageSize, setGroupPageSize] = useState(GROUP_PAGE_SIZES[0]);
  // The element that opened the drawer (a row, a ⋯ button): focus goes back to it when the drawer closes, so a
  // keyboard user keeps their place in a long register.
  const drawerOpenerRef = useRef(null);
  // Deep link /shop-drawings?tab=blocked&project=<project name> (Dashboard "Needs you today" › Review) opens
  // the Blocked tab filtered to that project (matched ignoring case and spacing, like the filter itself).
  const [blockedFilters, setBlockedFilters] = useState(() => {
    if (activeTab !== 'blocked') return EMPTY_BLOCKED_FILTERS;
    const owner = String(ownerParam || '').trim();
    return {
      ...EMPTY_BLOCKED_FILTERS,
      project: allState.project.length ? valueKey(allState.project[0]) || null : null,
      owner: owner ? (owner === '__none__' ? owner : valueKey(owner)) : null,
    };
  });
  useEffect(() => {
    // The project / owner now sit in the Blocked tab's own filters; drop them from the URL so the All tab
    // (which shares ?project=) is not filtered behind the person's back.
    if (activeTab === 'blocked' && allState.project.length) setAllState({ project: [] });
    if (ownerParam) setOwnerParam('');
    // Only on arrival.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [tidyOpen, setTidyOpen] = useState(false);
  // The Blocked list's sort lives here: "Export this view" writes the rows in the order the screen shows.
  const [blockedSort, setBlockedSort] = useState('since:desc');
  const [logFilters, setLogFilters] = useState(EMPTY_LOG_FILTERS);
  const [logSort, setLogSort] = useState('asc');
  const [drawerSubmissionOpen, setDrawerSubmissionOpen] = useState(false);
  const [editor, setEditor] = useState(null);
  const [selectedDrawingIds, setSelectedDrawingIds] = useState([]);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [bulkUpdating, setBulkUpdating] = useState(false);
  const [bulkDuplicating, setBulkDuplicating] = useState(false);
  const [bulkSubmission, setBulkSubmission] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [importRows, setImportRows] = useState([]);
  const [importing, setImporting] = useState(false);
  const [importError, setImportError] = useState('');
  const currentUserName = user?.name || user?.email || '';
  const disciplineOptions = useMemo(() => normalizeShopDrawingDisciplines(settings?.shop_drawing_disciplines || DISCIPLINES), [settings?.shop_drawing_disciplines]);

  const fetchDrawings = useCallback(async () => {
    try {
      const res = await shopDrawingsRequest('get');
      setDrawings(res.data || []);
    } catch (err) {
      if (!isNotFoundError(err)) {
        notify.error(errorDetail(err, 'Could not load shop drawings'));
      }
      setDrawings([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchDrawings(); }, [fetchDrawings]);

  /* ---------------------------------------------------------------- All Drawings view */

  const allFilters = useMemo(() => {
    const filters = { ...allState };
    delete filters.sort;
    delete filters.group;
    return filters;
  }, [allState]);
  const deferredQuery = useDeferredValue(allFilters.q);
  const allSort = parseSortParam(allState.sort) || { key: 'updated', dir: 'desc' };
  const grouped = allState.group !== 'none';
  const setAllFilters = useCallback((patch) => setAllState(patch), [setAllState]);
  const clearAllFilters = useCallback(() => setAllState({ ...EMPTY_ALL_FILTERS }), [setAllState]);

  const filteredDrawings = useMemo(
    () => filterDrawings(drawings, { ...allFilters, q: deferredQuery }),
    [drawings, allFilters, deferredQuery]
  );
  const filtersKey = JSON.stringify(allFilters);

  useEffect(() => { setAllPage(1); }, [filtersKey, allState.sort, allPageSize, groupPageSize, grouped]);

  // A selection only ever holds drawings that are in the current list (also across pages).
  useEffect(() => {
    const visible = new Set(filteredDrawings.map((drawing) => drawing.id));
    setSelectedDrawingIds((current) => {
      const next = current.filter((id) => visible.has(id));
      return next.length === current.length ? current : next;
    });
  }, [filteredDrawings]);

  useEffect(() => { setSelectedDrawingIds([]); }, [activeTab]);

  /* ---------------------------------------------------------------- derived */

  const pipeline = useMemo(() => computePipeline(drawings), [drawings]);
  const lastChange = useMemo(() => latestChange(drawings), [drawings]);
  const people = useMemo(() => {
    const values = uniqueValues(drawings, 'resp');
    if (currentUserName && !values.includes(currentUserName)) values.push(currentUserName);
    return values.sort((a, b) => a.localeCompare(b));
  }, [currentUserName, drawings]);
  const drawingTypes = useMemo(() => [...new Set([...DRAWING_TYPES, ...uniqueValues(drawings, 'dtype')])], [drawings]);
  const projectNames = useMemo(() => uniqueValues(drawings, 'project'), [drawings]);

  const blockedCount = useMemo(() => drawings.filter((drawing) => drawing.status === 'BLOCKED').length, [drawings]);
  const blockedRows = useMemo(() => filterBlocked(drawings, blockedFilters), [drawings, blockedFilters]);

  const submissionRows = useMemo(() => buildSubmissionRows(drawings), [drawings]);
  // 13 #9: what the "Tidy the register" callout and wizard offer.
  const tidy = useMemo(() => (loading ? null : tidySuggestions(drawings)), [drawings, loading]);
  const logBase = useMemo(() => filterSubmissionRows(submissionRows, logFilters), [submissionRows, logFilters]);
  const replyCounts = useMemo(() => {
    const counts = { awaiting: 0, replied: 0, not_recorded: 0, all: logBase.length };
    logBase.forEach((row) => { counts[row.replyState] += 1; });
    return counts;
  }, [logBase]);
  const logView = replyViewOf(logFilters, replyCounts);
  const logViewRows = useMemo(() => (logView === 'all' ? logBase : logBase.filter((row) => row.replyState === logView)), [logBase, logView]);
  const logRows = useMemo(
    () => orderSubmissionRows(logFilters.age ? logViewRows.filter((row) => ageBucket(row.sent) === logFilters.age) : logViewRows, logSort),
    [logViewRows, logFilters.age, logSort]
  );

  const currentView = activeTab === 'submission-log'
    ? { count: logRows.length, noun: 'submission' }
    : activeTab === 'blocked'
      ? { count: blockedRows.length, noun: 'drawing' }
      : { count: filteredDrawings.length, noun: 'drawing' };

  const detailDrawing = useMemo(() => (drawingParam ? drawings.find((drawing) => drawing.id === drawingParam) || null : null), [drawingParam, drawings]);

  /* ---------------------------------------------------------------- drawer + editor */

  const openDrawing = useCallback((drawing, { submission = false } = {}) => {
    // Remember where the person was: the table row (Enter or a click on it) — its ⋯ menu returns focus to the
    // row too — or whatever else had focus outside the drawer.
    const active = typeof document !== 'undefined' ? document.activeElement : null;
    const inDrawer = active?.closest?.('[data-testid="shop-drawing-drawer"]');
    if (!inDrawer) {
      // From a ⋯ menu ("Open details", "Log submission…") focus sits in the menu: use the row of its button.
      const menuTrigger = active?.closest?.('[role="menu"]') ? document.querySelector('[aria-haspopup="menu"][data-state="open"]') : null;
      const row = (menuTrigger || active)?.closest?.('tr[data-dt-index]');
      drawerOpenerRef.current = row || (active && active !== document.body && !menuTrigger && !active.closest?.('[role="menu"]') ? active : null);
    }
    setDrawerSubmissionOpen(submission);
    setDrawingParam(drawing.id);
  }, [setDrawingParam]);

  const closeDrawing = useCallback(({ restoreFocus = true } = {}) => {
    setDrawerSubmissionOpen(false);
    setDrawingParam('');
    const opener = drawerOpenerRef.current;
    drawerOpenerRef.current = null;
    // After the drawer has let go of focus (Radix moves it on unmount): back to the row that opened it.
    if (restoreFocus && opener) {
      window.setTimeout(() => {
        // Not when another dialog took focus meanwhile (a delete confirmation, the edit form).
        const dialog = document.activeElement?.closest?.('[role="dialog"], [role="alertdialog"]');
        if (dialog && dialog.getAttribute('data-testid') !== 'shop-drawing-drawer') return;
        if (!opener.isConnected) return;
        opener.focus({ preventScroll: true });
        const box = opener.getBoundingClientRect?.();
        if (box && (box.bottom < 0 || box.top > window.innerHeight)) opener.scrollIntoView?.({ block: 'center' });
      }, 0);
    }
  }, [setDrawingParam]);

  const openAddDrawing = () => {
    setEditor({ key: Date.now(), drawing: null, seed: { ...createDrawingDraft(), disc: disciplineOptions[0] || 'OTHER', updby: currentUserName }, returnTo: drawingParam });
  };

  const openEditDrawing = useCallback((drawing) => {
    setEditor({ key: Date.now(), drawing, seed: { ...createDrawingDraft(), ...drawing, updby: currentUserName, change_note: '' }, returnTo: drawingParam });
    setDrawingParam('');
  }, [currentUserName, drawingParam, setDrawingParam]);

  // Back to the drawer when the edit started there; an edit started from a row closes into the list.
  const closeEditor = useCallback(() => {
    const returnTo = editor?.returnTo;
    setEditor(null);
    if (returnTo) setDrawingParam(returnTo);
  }, [editor, setDrawingParam]);

  // The SidePanel closes itself after a successful save (closeEditor via onOpenChange). An edit's
  // `draft` holds only the changed fields + change_note; a new drawing's holds every field.
  const saveDrawing = async (draft) => {
    const editing = editor?.drawing;
    if (editing) {
      const res = await shopDrawingsRequest('put', `/${editing.id}`, { ...draft, updby: currentUserName });
      setDrawings((current) => current.map((item) => (item.id === res.data.id ? res.data : item)));
      notify.success(`“${drawingTitle(res.data)}” updated`);
    } else {
      const res = await shopDrawingsRequest('post', '', { ...draft, updby: draft.updby || currentUserName });
      setDrawings((current) => [res.data, ...current]);
      notify.success(`“${drawingTitle(res.data)}” added`);
    }
  };

  const saveSubmission = async (drawing, draft) => {
    const payload = { ...draft, date: toDayFirst(draft.date) };
    const res = await shopDrawingsRequest('post', `/${drawing.id}/submissions`, payload);
    setDrawings((current) => current.map((item) => (item.id === res.data.id ? res.data : item)));
    notify.success('Submission logged', { description: drawingTitle(res.data) });
  };

  /* ---------------------------------------------------------------- one-click changes */

  // Throws on failure: StatusMenu and the Blocked tab's Unblock show the error.
  const setStatus = useCallback(async (drawing, status) => {
    const label = labelize(status, STATUS_LABELS);
    const res = await shopDrawingsRequest('put', `/${drawing.id}`, { status, change_note: `Status set to ${label}` });
    setDrawings((current) => current.map((item) => (item.id === res.data.id ? res.data : item)));
    notify.success(`“${drawingTitle(res.data)}” set to ${label}`);
  }, []);

  // Throws on failure: the Blocked tab shows the error and keeps the selection.
  const bulkStatus = useCallback(async (ids, status) => {
    const label = labelize(status, STATUS_LABELS);
    const res = await shopDrawingsRequest('post', '/bulk-update', { ids, status, change_note: `Unblocked: status set to ${label}` });
    const items = res.data?.items || [];
    setDrawings((current) => mergeUpdated(current, items));
    notify.success(`${plural(items.length || ids.length, 'drawing')} set to ${label}`);
  }, []);

  const recordReplies = useCallback(async (rows, form) => {
    if (!rows.length) return;
    const payload = { reply: form.reply, reply_date: toDayFirst(form.date), comments: form.comments || '' };
    const code = REPLY_CODE_OF[form.reply];
    if (rows.length === 1) {
      const [row] = rows;
      const res = await shopDrawingsRequest('put', `/${row.drawing.id}/submissions/${row.index}`, {
        ...payload,
        submission_id: row.submission.id || null,
        previous_reply: row.submission.reply || 'PENDING',
      });
      setDrawings((current) => current.map((item) => (item.id === res.data.id ? res.data : item)));
      notify.success(`Reply recorded: ${code ? `${code} · ` : ''}${labelize(form.reply, { APPROVED_WITH_COMMENTS: 'Approved w/ comments', REVISE_AND_RESUBMIT: 'Revise & resubmit' })}`, { description: drawingTitle(res.data) });
      return;
    }
    let res;
    try {
      res = await shopDrawingsRequest('post', '/bulk-replies', {
        ...payload,
        items: rows.map((row) => ({ id: row.drawing.id, index: row.index, submission_id: row.submission.id || null, previous_reply: row.submission.reply || 'PENDING' })),
      });
    } catch (err) {
      throw new Error(bulkErrorMessage(err, 'Could not save the reply'));
    }
    setDrawings((current) => mergeUpdated(current, res.data?.items || []));
    const skipped = res.data?.skipped_count || 0;
    notify.success(`Reply recorded on ${plural(res.data?.updated_count || rows.length, 'submission')}`, {
      description: skipped ? `${plural(skipped, 'submission')} changed meanwhile and ${skipped === 1 ? 'was' : 'were'} skipped.` : undefined,
    });
  }, []);

  const markImportAwaiting = useCallback(async (expected) => {
    const res = await shopDrawingsRequest('post', '/import-replies/mark-awaiting', { expected });
    setDrawings((current) => mergeUpdated(current, res.data?.items || []));
    notify.success(`${plural(res.data?.updated_count || 0, 'import reply', 'import replies')} marked as awaiting reply`);
  }, []);

  /* ---------------------------------------------------------------- wave 3 */

  /**
   * Several drawings' changes through the existing /bulk-edit (13 #9 tidy, 14 #7 suggestions, 14 #9 board):
   * at most 500 per request, each request one undo step named after `purpose` ('tidy' | 'suggestions' |
   * 'reasons'; "Recent changes" shows it). Throws a readable Error (the dialogs show it).
   */
  const applyChanges = useCallback(async (changes, { message, purpose } = {}) => {
    const list = (changes || []).filter(Boolean);
    if (!list.length) return;
    let done = 0;
    for (let index = 0; index < list.length; index += BULK_LIMIT) {
      const chunk = list.slice(index, index + BULK_LIMIT);
      try {
        const res = await shopDrawingsRequest('post', '/bulk-edit', purpose ? { drawings: chunk, purpose } : { drawings: chunk });
        setDrawings((current) => mergeUpdated(current, res.data?.items || []));
        done += res.data?.updated_count || chunk.length;
      } catch (err) {
        const reason = bulkErrorMessage(err, 'Could not save the changes');
        throw new Error(done ? `${plural(done, 'drawing')} saved, the rest failed: ${reason}` : reason);
      }
    }
    notify.success(message || `${plural(done, 'drawing')} updated`);
  }, []);

  const applyTidy = useCallback(
    (chunks) => applyChanges(chunks.flat(), { purpose: 'tidy', message: `Register tidied: ${plural(chunks.flat().length, 'drawing')} updated` }),
    [applyChanges]
  );

  // 14 #9 "Request info": send from Sara (Outgoing email) or log an email sent from the person's own mail app.
  // A logged request can be undone (the toast offers it); a SENT one cannot — the server records no undo step.
  const requestInfo = useCallback(async (payload) => {
    const res = await shopDrawingsRequest('post', '/request-info', payload);
    setDrawings((current) => mergeUpdated(current, res.data?.items || []));
    const count = res.data?.updated_count || payload.ids.length;
    const where = `the history of ${count === 1 ? 'the drawing' : 'each drawing'} records it`;
    notify.success(res.data?.sent ? `Request sent to ${(res.data.to || []).join(', ')}` : 'Info request logged', {
      description: res.data?.sent ? `An email cannot be unsent: ${where}.` : `${plural(count, 'drawing')} · ${where}`,
    });
  }, []);

  // 15 #9 "Chase": the same, for submissions awaiting the consultant's reply.
  const chaseConsultant = useCallback(async (group, payload) => {
    const res = await shopDrawingsRequest('post', '/chase', {
      ...payload,
      items: group.rows.map((row) => ({ id: row.drawing.id, index: row.index, submission_id: row.submission.id || null })),
      recipient: group.recipient,
      project: group.project,
    });
    setDrawings((current) => mergeUpdated(current, res.data?.items || []));
    const count = res.data?.updated_count || group.rows.length;
    notify.success(res.data?.sent ? `Follow-up sent to ${(res.data.to || []).join(', ')}` : 'Chase logged', {
      description: res.data?.sent
        ? `${plural(count, 'submission')} marked as chased · an email cannot be unsent`
        : `${plural(count, 'submission')} marked as chased`,
    });
  }, []);

  /* ---------------------------------------------------------------- selection + bulk */

  const selectedDrawings = useMemo(() => {
    const set = new Set(selectedDrawingIds);
    return drawings.filter((drawing) => set.has(drawing.id));
  }, [drawings, selectedDrawingIds]);

  const deleteDrawings = useCallback(async (ids) => {
    if (!ids.length) return false;
    const chosen = drawings.filter((drawing) => ids.includes(drawing.id));
    const withSubmissions = chosen.filter((drawing) => (drawing.submissions || []).length).length;
    const projects = [...new Set(chosen.map((drawing) => drawing.project).filter(Boolean))];
    const ok = await confirm({
      title: ids.length === 1 ? `Delete “${drawingTitle(chosen[0] || {})}”?` : `Delete ${fmt(ids.length)} drawings?`,
      subtitle: projects.length ? projects.slice(0, 3).join(' · ') + (projects.length > 3 ? ` +${projects.length - 3} more` : '') : undefined,
      description: ids.length === 1
        ? 'This removes it from the register together with its history:'
        : 'This removes them from the register together with their history:',
      consequences: [
        { icon: Files, count: ids.length, label: ids.length === 1 ? 'drawing' : 'drawings' },
        withSubmissions ? { icon: Send, count: withSubmissions, label: withSubmissions === 1 ? 'has logged submissions' : 'have logged submissions' } : null,
      ].filter(Boolean),
      tone: 'danger',
      confirmLabel: ids.length === 1 ? 'Delete drawing' : `Delete ${fmt(ids.length)} drawings`,
      note: 'Changed your mind? Use Undo on the message that appears right after.',
      onConfirm: async () => {
        await shopDrawingsRequest('post', '/bulk-delete', { ids });
      },
    });
    if (!ok) return false;
    setDrawings((current) => current.filter((item) => !ids.includes(item.id)));
    if (drawingParam && ids.includes(drawingParam)) closeDrawing();
    setSelectedDrawingIds((current) => current.filter((id) => !ids.includes(id)));
    notify.success(ids.length === 1 ? 'Drawing deleted' : `${fmt(ids.length)} drawings deleted`);
    return true;
  }, [closeDrawing, confirm, drawingParam, drawings]);

  const deleteSelectedDrawings = async () => {
    setBulkDeleting(true);
    try {
      await deleteDrawings(selectedDrawingIds.filter(Boolean));
    } finally {
      setBulkDeleting(false);
    }
  };

  const bulkUpdateSelectedDrawings = async (field, value) => {
    const ids = selectedDrawingIds.filter(Boolean);
    if (!ids.length || !value) return;
    setBulkUpdating(true);
    try {
      const label = field === 'status' ? labelize(value, STATUS_LABELS) : value;
      const payload = {
        ids,
        change_note: field === 'status' ? `Bulk status update to ${label}` : `Bulk responsible assignment to ${value}`,
        [field]: value,
      };
      const res = await shopDrawingsRequest('post', '/bulk-update', payload);
      const updatedItems = res.data?.items || [];
      setDrawings((current) => mergeUpdated(current, updatedItems));
      setSelectedDrawingIds([]);
      const count = updatedItems.length || ids.length;
      notify.success(field === 'status'
        ? `${plural(count, 'drawing')} set to ${label}`
        : `${plural(count, 'drawing')} assigned to ${label}`);
    } catch (err) {
      notify.error('Could not update the selected drawings', { description: getErrorMessage(err) });
    } finally {
      setBulkUpdating(false);
    }
  };

  const duplicateDrawings = useCallback(async (ids, { select = true } = {}) => {
    if (!ids.length || ids.length > BULK_LIMIT) return;
    setBulkDuplicating(true);
    try {
      const res = await shopDrawingsRequest('post', '/bulk-duplicate', { ids });
      const duplicatedItems = res.data?.items || [];
      setDrawings((current) => [...duplicatedItems, ...current]);
      if (select) setSelectedDrawingIds(duplicatedItems.map((item) => item.id).filter(Boolean));
      notify.success(`${plural(duplicatedItems.length || ids.length, 'drawing')} duplicated`, { description: select ? 'The copies are selected.' : duplicatedItems.map((item) => item.id).join(', ') });
    } catch (err) {
      notify.error('Could not duplicate', { description: bulkErrorMessage(err) });
    } finally {
      setBulkDuplicating(false);
    }
  }, []);

  const logBulkSubmission = async (form, ids) => {
    let res;
    try {
      res = await shopDrawingsRequest('post', '/bulk-submissions', { ids, ...form });
    } catch (err) {
      throw new Error(bulkErrorMessage(err, 'Could not log the submission'));
    }
    const updatedItems = res.data?.items || [];
    setDrawings((current) => mergeUpdated(current, updatedItems));
    setSelectedDrawingIds([]);
    notify.success(`Submission logged for ${plural(updatedItems.length || ids.length, 'drawing')}`);
  };

  const exportSelected = () => {
    if (!selectedDrawings.length) return;
    downloadXlsx(drawingsToXlsx(selectedDrawings), `shop-drawings-selected-${new Date().toISOString().slice(0, 10)}.xlsx`);
    notify.success(`Exported ${plural(selectedDrawings.length, 'drawing')}`);
  };

  const copyLink = useCallback(async (drawing) => {
    const url = `${window.location.origin}/shop-drawings?drawing=${encodeURIComponent(drawing.id)}`;
    try {
      await copyText(url);
      notify.success('Link copied', { description: url });
    } catch {
      notify.error('Could not copy the link', { description: url });
    }
  }, []);

  /** The ⋯ menu of a drawing (rows of every tab and the drawer); read-only: Open details and Copy link. */
  const rowMenuItems = useCallback((drawing) => (readOnly ? [
    { label: 'Open details', icon: FolderOpen, onSelect: () => openDrawing(drawing) },
    { label: 'Copy link', icon: Link2, onSelect: () => copyLink(drawing) },
  ] : [
    { label: 'Open details', icon: FolderOpen, onSelect: () => openDrawing(drawing) },
    { label: 'Log submission…', icon: Send, onSelect: () => openDrawing(drawing, { submission: true }) },
    { label: 'Edit drawing', icon: Pencil, onSelect: () => openEditDrawing(drawing) },
    { label: 'Duplicate', icon: Copy, onSelect: () => duplicateDrawings([drawing.id], { select: false }), testId: `duplicate-${drawing.id}` },
    { label: 'Copy link', icon: Link2, onSelect: () => copyLink(drawing) },
    { label: 'Delete drawing…', icon: Trash2, danger: true, onSelect: () => deleteDrawings([drawing.id]), testId: `delete-${drawing.id}` },
  ]), [copyLink, deleteDrawings, duplicateDrawings, openDrawing, openEditDrawing, readOnly]);

  const logSubmissionFor = useCallback((drawing) => openDrawing(drawing, { submission: true }), [openDrawing]);
  const replyFor = useCallback((drawing) => openDrawing(drawing, { submission: !(drawing.submissions || []).length }), [openDrawing]);

  /* ---------------------------------------------------------------- blocked + log actions */

  const setBlockedReasons = async (ids, blocker) => {
    const idSet = new Set(ids);
    const { note, single, many } = reasonWords(blocker);
    const changes = drawings
      .filter((drawing) => idSet.has(drawing.id) && drawing.blocker !== blocker)
      .map((drawing) => ({ id: drawing.id, blocker, change_note: note }));
    if (!changes.length) return;
    try {
      const res = await shopDrawingsRequest('post', '/bulk-edit', { drawings: changes, purpose: 'reasons' });
      setDrawings((current) => mergeUpdated(current, res.data?.items || []));
      notify.success(changes.length === 1 ? single : many(changes.length));
    } catch (err) {
      throw new Error(getErrorMessage(err, 'Could not save the reasons'));
    }
  };

  /** "Set blocker reason": `owner` is undefined unless the person changed it in the popover. */
  const saveReason = useCallback(async ({ ids, blocker, note, owner }) => {
    const idSet = new Set(ids);
    const words = reasonWords(blocker);
    const changes = drawings
      .filter((drawing) => idSet.has(drawing.id))
      .map((drawing) => {
        const change = { id: drawing.id };
        if (drawing.blocker !== blocker) change.blocker = blocker;
        if (note && note !== String(drawing.action || '').trim()) change.action = note;
        if (owner !== undefined && owner !== String(drawing.resp || '').trim()) change.resp = owner;
        return change;
      })
      .filter((change) => Object.keys(change).length > 1)
      .map((change) => ({ ...change, change_note: words.note }));
    if (!changes.length) return;
    const res = await shopDrawingsRequest('post', '/bulk-edit', { drawings: changes, purpose: 'reasons' });
    setDrawings((current) => mergeUpdated(current, res.data?.items || []));
    notify.success(changes.length === 1 ? words.single : words.many(changes.length));
  }, [drawings]);

  const fixSubmissionDate = async (row, dayFirst) => {
    const res = await shopDrawingsRequest('patch', `/${row.drawing.id}/submissions/${row.index}`, {
      date: dayFirst,
      previous_date: String(row.submission.date ?? ''),
    });
    setDrawings((current) => current.map((item) => (item.id === res.data.id ? res.data : item)));
    notify.success(`Date sent corrected to ${formatDate(dayFirst)}`, { description: drawingTitle(res.data) });
  };

  /* ---------------------------------------------------------------- excel */

  const handleImportFile = async (file) => {
    setImportError('');
    setImportRows([]);
    if (!file) return;
    try {
      const rows = await parseImportFile(file);
      if (!rows.length) {
        setImportError('No readable rows found. Check the Shop Drawings sheet and Unit Description column.');
        return;
      }
      setImportRows(rows);
    } catch (err) {
      console.error('Shop drawings import parse failed', err);
      setImportError(err.message || 'Could not read this file. Please check the file and try again.');
    }
  };

  const confirmImport = async () => {
    if (!importRows.length) return;
    setImporting(true);
    try {
      const res = await shopDrawingsRequest('post', '/bulk', { drawings: importRows });
      setDrawings((current) => [...(res.data || []), ...current]);
      setImportOpen(false);
      setImportRows([]);
      notify.success(`Imported ${plural((res.data || []).length, 'drawing')}`);
    } catch (err) {
      notify.error('Could not import the drawings', { description: getErrorMessage(err) });
    } finally {
      setImporting(false);
    }
  };

  const exportCurrentView = () => {
    const stamp = new Date().toISOString().slice(0, 10);
    let bytes;
    let name;
    if (activeTab === 'submission-log') {
      if (!logRows.length) return notify.error('Nothing to export in this view');
      bytes = submissionsToXlsx(logRows);
      name = `shop-drawings-submission-log-${stamp}.xlsx`;
    } else if (activeTab === 'blocked') {
      if (!blockedRows.length) return notify.error('Nothing to export in this view');
      // As the screen shows them: longest blocked first (or the chosen sort), project by project; "Reason missing"
      // and a blank Action note where the screen shows them so.
      bytes = drawingsToXlsx(blockedViewOrder(blockedRows, blockedSort), { blocked: true });
      name = `shop-drawings-blocked-${stamp}.xlsx`;
    } else {
      if (!filteredDrawings.length) return notify.error('Nothing to export in this view');
      bytes = drawingsToXlsx(filteredDrawings);
      name = `shop-drawings-${stamp}.xlsx`;
    }
    downloadXlsx(bytes, name);
    return notify.success(`Exported ${plural(currentView.count, currentView.noun)}`, { description: name });
  };

  const downloadTemplate = () => {
    downloadXlsx(templateToXlsx(), 'shop-drawings-import-template.xlsx');
    notify.success('Template downloaded', { description: 'Fill the “Shop Drawings” sheet, then use Import from Excel.' });
  };

  /* ---------------------------------------------------------------- render */

  const selectPipeline = (key) => {
    setAllState({ stage: key || '' });
    if (key) setActiveTab('all');
  };

  const subtitle = loading
    ? 'Track every drawing from first draft to consultant approval'
    : [
      'Track every drawing from first draft to consultant approval',
      plural(pipeline.projects, 'project'),
      lastChange ? `last change ${lastChange.when}${lastChange.who ? ` by ${lastChange.who}` : ''}` : null,
    ].filter(Boolean).join(' · ');
  const selectionProjects = [...new Set(selectedDrawings.map((drawing) => drawing.project).filter(Boolean))];
  const bulkBusy = bulkDeleting || bulkUpdating || bulkDuplicating;
  const overBulkLimit = selectedDrawingIds.length > BULK_LIMIT;
  const overLimitTitle = overBulkLimit ? `Works on up to ${fmt(BULK_LIMIT)} drawings at a time (${fmt(selectedDrawingIds.length)} selected)` : undefined;
  // `pageIds`: the drawings of the page on screen (50 drawings, or the projects of a grouped page) — once all of
  // them are selected the bar offers "Select all N".
  const renderAllBulkBar = ({ pageIds = [] } = {}) => {
    const pageFullySelected = pageIds.length > 0 && pageIds.every((id) => selectedDrawingIds.includes(id));
    return (
      <BulkBar
        count={selectedDrawingIds.length}
        label={selectionProjects.length === 1 ? <>selected <span className="font-medium text-white/70">in {selectionProjects[0]}</span></> : 'selected'}
        onClear={() => setSelectedDrawingIds([])}
      >
        {pageFullySelected && selectedDrawingIds.length < filteredDrawings.length ? (
          <button type="button" onClick={() => setSelectedDrawingIds(filteredDrawings.map((drawing) => drawing.id))} className="mr-1 whitespace-nowrap rounded text-[12.5px] font-semibold text-[color:var(--navy-100)] underline decoration-white/40 underline-offset-2 hover:text-white hover:decoration-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/60" data-testid="select-all-filtered-shop-drawings">
            Select all {fmt(filteredDrawings.length)}
          </button>
        ) : null}
        {overBulkLimit ? (
          <span className="mr-1 whitespace-nowrap text-[12px] font-medium text-white/75" data-testid="bulk-limit-hint">
            Duplicate and Log submission: up to {fmt(BULK_LIMIT)} at a time
          </span>
        ) : null}
        <Select value={BULK_STATUS_PLACEHOLDER} onValueChange={(value) => value !== BULK_STATUS_PLACEHOLDER && bulkUpdateSelectedDrawings('status', value)} disabled={bulkBusy}>
          <SelectTrigger className="h-[30px] w-auto gap-1.5 rounded-lg border-0 bg-white/10 px-2.5 text-[12.5px] font-semibold text-white shadow-none hover:bg-white/20 focus:ring-white/60 [&>svg]:opacity-90" data-testid="bulk-update-status-select" aria-label="Set status">
            <SelectValue placeholder="Set status" />
          </SelectTrigger>
          <SelectContent className="rounded-xl border-line bg-surface p-1 text-ink shadow-overlay">
            <SelectItem value={BULK_STATUS_PLACEHOLDER} disabled className="hidden">Set status</SelectItem>
            {STATUSES.map((status) => (
              <SelectItem key={status} value={status} className="rounded-lg text-[13px]">
                <span className="inline-flex items-center gap-2">
                  <span className={cn('ui-tone-dot', `tone-${getStatusTone(status, 'shop_drawing')}`)} style={{ width: 8, height: 8 }} aria-hidden="true" />
                  {labelize(status, STATUS_LABELS)}
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={BULK_RESP_PLACEHOLDER} onValueChange={(value) => value !== BULK_RESP_PLACEHOLDER && bulkUpdateSelectedDrawings('resp', value)} disabled={bulkBusy || people.length === 0}>
          <SelectTrigger className="h-[30px] w-auto gap-1.5 rounded-lg border-0 bg-white/10 px-2.5 text-[12.5px] font-semibold text-white shadow-none hover:bg-white/20 focus:ring-white/60 [&>svg]:opacity-90" data-testid="bulk-assign-responsible-select" aria-label="Assign responsible">
            <UserPlus className="h-3.5 w-3.5" aria-hidden="true" />
            <SelectValue placeholder="Assign" />
          </SelectTrigger>
          <SelectContent className="rounded-xl border-line bg-surface p-1 text-ink shadow-overlay">
            <SelectItem value={BULK_RESP_PLACEHOLDER} disabled className="hidden">Assign</SelectItem>
            {people.map((name) => <SelectItem key={name} value={name} className="rounded-lg text-[13px]">{name}</SelectItem>)}
          </SelectContent>
        </Select>
        <BulkButton icon={Send} onClick={() => setBulkSubmission({ drawings: selectedDrawings })} disabled={bulkBusy || overBulkLimit} title={overLimitTitle} data-testid="bulk-log-submission-btn">Log submission</BulkButton>
        <BulkButton icon={Copy} onClick={() => duplicateDrawings(selectedDrawingIds.filter(Boolean))} disabled={bulkBusy || overBulkLimit} title={overLimitTitle} data-testid="duplicate-selected-shop-drawings-btn">
          {bulkDuplicating ? 'Duplicating...' : 'Duplicate'}
        </BulkButton>
        <BulkDivider />
        <RowActionsForBulk
          disabled={bulkBusy}
          count={selectedDrawings.length}
          ids={selectedDrawingIds}
          onExport={exportSelected}
          onDelete={deleteSelectedDrawings}
        />
      </BulkBar>
    );
  };

  // 15: "68 Submitted not logged ↗" — All Drawings, Submitted drawings without a logged submission.
  const submittedNoLog = useMemo(() => submittedNotLogged(drawings).length, [drawings]);
  const showSubmittedNotLogged = useCallback(() => {
    setAllState({ ...EMPTY_ALL_FILTERS, status: ['SUBMITTED'], nolog: true });
    setViewParam('');
    setActiveTab('all');
  }, [setActiveTab, setAllState, setViewParam]);

  return (
    <div data-testid={loading ? 'shop-drawings-page-loading' : 'shop-drawings-page'} aria-busy={loading || undefined} className="pb-6">
      <PageHeader
        title="Shop Drawings"
        count={loading ? undefined : fmt(drawings.length)}
        subtitle={subtitle}
        actions={(
          <>
            <DropdownMenu modal={false}>
              <DropdownMenuTrigger asChild>
                <Button type="button" variant="outline" data-testid="shop-drawings-excel-menu">
                  <FileSpreadsheet aria-hidden="true" /> Excel <ChevronDown className="!size-3.5 text-ink-3" aria-hidden="true" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="min-w-[250px] rounded-xl border-line bg-surface p-1.5 text-ink shadow-overlay">
                {readOnly ? null : (
                  <DropdownMenuItem className={MENU_ITEM} onSelect={() => setTimeout(() => setImportOpen(true), 0)} data-testid="import-shop-drawings-btn">
                    <Upload aria-hidden="true" /> Import from Excel
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem className={MENU_ITEM} onSelect={exportCurrentView} disabled={!currentView.count} data-testid="export-shop-drawings-btn">
                  <Download aria-hidden="true" />
                  <span className="flex-1">Export this view</span>
                  <span className="text-[11.5px] font-semibold tabular-nums text-ink-3">{plural(currentView.count, 'row')}</span>
                </DropdownMenuItem>
                {readOnly ? null : (
                  <>
                    <DropdownMenuSeparator className="my-1 bg-line-2" />
                    <DropdownMenuItem className={MENU_ITEM} onSelect={downloadTemplate} data-testid="download-shop-drawings-template-btn">
                      <FileDown aria-hidden="true" /> Download template
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
            {readOnly ? null : (
              <Button type="button" onClick={openAddDrawing} data-testid="add-shop-drawing-btn">
                <Plus aria-hidden="true" /> Add drawing
              </Button>
            )}
          </>
        )}
      />

      <div className="mt-5">
        <PipelineStrip pipeline={pipeline} activeKey={allFilters.stage || null} onSelect={selectPipeline} loading={loading} />
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-5">
        <TabsList className="flex h-auto w-full justify-start gap-[22px] overflow-x-auto rounded-none border-b border-line bg-transparent p-0 text-ink-3">
          <SdTab value="all" label="All Drawings" count={loading ? null : drawings.length} active={activeTab === 'all'} />
          <SdTab value="blocked" label="Blocked" count={loading ? null : blockedCount} active={activeTab === 'blocked'} alert={blockedCount > 0} />
          <SdTab value="submission-log" label="Submission Log" count={loading ? null : submissionRows.length} active={activeTab === 'submission-log'} />
        </TabsList>

        <TabsContent value="all" className="mt-4 focus-visible:ring-0 focus-visible:ring-offset-0">
          <AllDrawingsTab
            loading={loading}
            drawings={drawings}
            rows={filteredDrawings}
            filters={allFilters}
            onFiltersChange={setAllFilters}
            onClearFilters={clearAllFilters}
            grouped={grouped}
            onGroupedChange={(value) => setAllState({ group: value ? 'project' : 'none' })}
            sort={allSort}
            onSortChange={(next) => setAllState({ sort: formatSortParam(next) || 'updated:desc' })}
            page={allPage}
            pageSize={allPageSize}
            onPage={setAllPage}
            onPageSize={setAllPageSize}
            groupPageSize={groupPageSize}
            onGroupPageSize={setGroupPageSize}
            selectedIds={selectedDrawingIds}
            onSelectionChange={setSelectedDrawingIds}
            onOpen={openDrawing}
            onEdit={openEditDrawing}
            onLogSubmission={logSubmissionFor}
            onSetStatus={setStatus}
            onReply={replyFor}
            rowMenuItems={rowMenuItems}
            renderBulkBar={renderAllBulkBar}
            activeId={detailDrawing?.id}
            projectHref={projectHref}
            onAdd={openAddDrawing}
            onImport={() => setImportOpen(true)}
            view={viewParam === 'matrix' ? 'matrix' : 'list'}
            onViewChange={(next) => setViewParam(next === 'matrix' ? 'matrix' : '')}
            tidy={readOnly ? null : tidy}
            onReviewTidy={() => setTidyOpen(true)}
            readOnly={readOnly}
          />
        </TabsContent>

        <TabsContent value="blocked" className="mt-4 focus-visible:ring-0 focus-visible:ring-offset-0">
          <BlockedTab
            drawings={drawings}
            rows={blockedRows}
            filters={blockedFilters}
            onFiltersChange={setBlockedFilters}
            loading={loading}
            onOpen={openDrawing}
            onSetReasons={setBlockedReasons}
            onSaveReason={saveReason}
            onSetStatus={setStatus}
            onBulkStatus={bulkStatus}
            rowMenuItems={rowMenuItems}
            people={people}
            onExport={exportCurrentView}
            view={viewParam === 'board' ? 'board' : 'list'}
            onViewChange={(next) => setViewParam(next === 'board' ? 'board' : '')}
            onApplyChanges={readOnly ? null : applyChanges}
            onRequestInfo={readOnly ? null : requestInfo}
            currentUserName={currentUserName}
            sortValue={blockedSort}
            onSortValueChange={setBlockedSort}
            readOnly={readOnly}
          />
        </TabsContent>

        <TabsContent value="submission-log" className="mt-4 focus-visible:ring-0 focus-visible:ring-offset-0">
          <SubmissionLogTab
            allRows={submissionRows}
            rows={logRows}
            viewRows={logViewRows}
            counts={replyCounts}
            filters={logFilters}
            onFiltersChange={setLogFilters}
            sort={logSort}
            onSortChange={setLogSort}
            loading={loading}
            isAdmin={isAdmin && !readOnly}
            onOpenDrawing={openDrawing}
            onFixDate={fixSubmissionDate}
            onRecordReply={recordReplies}
            onMarkImportAwaiting={markImportAwaiting}
            onLogTransmittal={() => setBulkSubmission({ drawings: [], pick: true })}
            onExport={exportCurrentView}
            rowMenuItems={rowMenuItems}
            chaseRows={logBase}
            onChase={readOnly ? null : chaseConsultant}
            currentUserName={currentUserName}
            submittedNotLogged={loading ? 0 : submittedNoLog}
            onShowSubmittedNotLogged={showSubmittedNotLogged}
            readOnly={readOnly}
          />
        </TabsContent>
      </Tabs>

      <DrawingPanel
        drawing={detailDrawing}
        open={Boolean(detailDrawing)}
        onOpenChange={(open) => { if (!open) closeDrawing(); }}
        onEdit={readOnly ? null : openEditDrawing}
        menuItems={detailDrawing ? rowMenuItems(detailDrawing).filter((item) => item.label !== 'Open details' && item.label !== 'Edit drawing' && item.label !== 'Log submission…') : []}
        onSaveSubmission={readOnly ? null : saveSubmission}
        onRecordReply={readOnly ? null : recordReplies}
        currentUserName={currentUserName}
        submissionOpen={drawerSubmissionOpen}
        onSubmissionOpenChange={setDrawerSubmissionOpen}
      />
      {editor && !readOnly ? (
        <DrawingEditPanel
          key={editor.key}
          open
          onOpenChange={(open) => { if (!open) closeEditor(); }}
          drawing={editor.drawing}
          seed={editor.seed}
          disciplineOptions={disciplineOptions}
          drawingTypes={drawingTypes}
          people={people}
          projects={projectNames}
          currentUserName={currentUserName}
          onSave={saveDrawing}
        />
      ) : null}
      <BulkSubmissionDialog
        open={Boolean(bulkSubmission) && !readOnly}
        onOpenChange={(open) => { if (!open) setBulkSubmission(null); }}
        drawings={bulkSubmission?.drawings || []}
        pickFrom={bulkSubmission?.pick ? drawings : null}
        currentUserName={currentUserName}
        onSave={logBulkSubmission}
      />
      <ImportDialog open={importOpen && !readOnly} onOpenChange={setImportOpen} rows={importRows} error={importError} importing={importing} onFile={handleImportFile} onImport={confirmImport} />
      <TidyDialog open={tidyOpen && !readOnly} onOpenChange={setTidyOpen} drawings={drawings} tidy={tidy} people={people} onApply={applyTidy} onOpenDrawing={openDrawing} />
    </div>
  );
}

function RowActionsForBulk({ disabled, count, ids, onExport, onDelete }) {
  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <BulkButton
          disabled={disabled}
          aria-label="More actions for the selection"
          title="More actions"
          className="w-[30px] justify-center px-0 data-[state=open]:bg-white/25"
          data-testid="bulk-more-actions"
        >
          <span aria-hidden="true" className="text-[15px] leading-none">⋯</span>
        </BulkButton>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" side="top" className="min-w-[220px] rounded-xl border-line bg-surface p-1.5 text-ink shadow-overlay">
        <DropdownMenuItem className={MENU_ITEM} onSelect={onExport} data-testid="export-selected-shop-drawings-btn">
          <Download aria-hidden="true" /> Export selected ({fmt(count)})
        </DropdownMenuItem>
        <DropdownMenuSeparator className="my-1 bg-line-2" />
        <DropdownMenuItem className={cn(MENU_ITEM, 'text-status-red-ink focus:text-status-red-ink [&>svg]:text-status-red-ink')} onSelect={onDelete} data-testid="delete-selected-shop-drawings-btn">
          <Trash2 aria-hidden="true" /> {ids.length === 1 ? 'Delete drawing…' : `Delete ${fmt(ids.length)} drawings…`}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function SdTab({ value, label, count, active, alert = false }) {
  return (
    <TabsTrigger
      value={value}
      className={cn(
        'relative h-auto shrink-0 gap-[7px] rounded-none bg-transparent px-0 pb-[11px] pt-2.5 text-[13.5px] font-semibold text-ink-3 shadow-none ring-offset-0 hover:text-ink',
        'data-[state=active]:bg-transparent data-[state=active]:text-ink data-[state=active]:shadow-none',
        'after:absolute after:inset-x-0 after:-bottom-px after:h-[2.5px] after:rounded-t-sm after:bg-transparent data-[state=active]:after:bg-brand'
      )}
    >
      {label}
      {count !== null && count !== undefined ? (
        <span
          className={cn(
            'rounded-full px-[7px] py-px text-[11px] font-bold tabular-nums',
            active ? 'bg-brand text-brand-fg' : alert ? 'bg-status-red-bg text-status-red-ink' : 'bg-[color:var(--status-gray-bg)] text-ink-2'
          )}
          data-testid={`tab-count-${value}`}
        >
          {fmt(count)}
        </span>
      ) : null}
    </TabsTrigger>
  );
}

function ImportDialog({ open, onOpenChange, rows, error, importing, onFile, onImport }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] max-w-3xl overflow-y-auto rounded-2xl border-line bg-surface text-ink" aria-describedby={undefined}>
        <DialogHeader><DialogTitle className="font-heading text-[17px] font-bold text-ink">Import from Excel</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <label className="flex cursor-pointer flex-col items-center justify-center rounded-xl border border-dashed border-[color:var(--line-dashed)] bg-surface-2 px-4 py-8 text-center hover:bg-surface-hover">
            <Upload className="mb-2 h-6 w-6 text-ink-3" aria-hidden="true" />
            <span className="text-[13.5px] font-semibold text-ink">Choose .xlsx, .csv, or .tsv file</span>
            <span className="mt-1 text-[12.5px] text-ink-3">XLSX imports read the “Shop Drawings” sheet. A row with Submitted = YES gets a submission whose reply follows its status (Awaiting reply unless the drawing is approved).</span>
            <Input data-testid="shop-drawings-import-file" type="file" accept=".xlsx,.xls,.csv,.tsv,.txt" className="hidden" onChange={(event) => onFile(event.target.files?.[0])} />
          </label>
          {error ? <p className="rounded-lg border border-[color:var(--danger-border)] bg-status-red-bg px-3 py-2 text-sm text-status-red-ink" role="alert">{error}</p> : null}
          {rows.length ? (
            <div className="overflow-hidden rounded-xl border border-line bg-surface">
              <div className="border-b border-line-2 px-4 py-3 text-[12.5px] text-ink-3">Preview of the first {Math.min(rows.length, 5)} of {plural(rows.length, 'row')}</div>
              <div className="overflow-x-auto">
                <table className={tableClass}>
                  <thead>
                    <tr>
                      {['Discipline', 'Project', 'Block', 'Description', 'Status'].map((head) => <th key={head} className={thClass} scope="col">{head}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.slice(0, 5).map((row, index) => (
                      <tr key={`${row.desc}-${index}`}>
                        <td className={tdClass}><DisciplineChip disc={row.disc} /></td>
                        <td className={cn(tdClass, 'text-xs text-ink-2')}>{row.project || '—'}</td>
                        <td className={cn(tdClass, 'text-xs text-ink-2')}>{row.block || '—'}</td>
                        <td className={cn(tdClass, 'text-xs font-semibold text-ink')}>{row.desc}</td>
                        <td className={tdClass}><StatusPill status={row.status} domain="shop_drawing" label={labelize(row.status, STATUS_LABELS)} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : null}
          {importing ? (
            <div className="h-1.5 overflow-hidden rounded-full bg-[color:var(--line-2)]">
              <div className="h-1.5 w-full animate-pulse rounded-full bg-brand" />
            </div>
          ) : null}
          <Button type="button" disabled={!rows.length || importing} onClick={onImport} className="w-full">
            Import {rows.length} rows
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

